<script setup lang="ts">
import { useStepper } from "@vueuse/core";
import useAsync from "~/hooks/use-async";
import type { CardTokenParams } from "~/modules/mercadopago/module";
import BillingAPI from "~/services/billing";
import useEventStore from "~/stores/event";
import { getCallbackCommerceUrl } from "~/utils/commerce";
import getErrorLocalePathMessage from "~/utils/locale";

const route = useRoute();
const toaster = useToast();
const { t, te } = useI18n();
const eventStore = useEventStore();
const appConfig = useRuntimeConfig().public;

const userCommerce = reactive({
  activeChosenSubscription: null as null | IModelCommerce,
  isCommerceBuying: false,
  successPurchased: false,
  errorPurchased: false,
});
const hasMercadoPagoPayment = appConfig.APP_CUSTOMER.enable_mercadopago_payment;
const noModalBackDropClick = ref(false);
const newCreditCard = ref<CardTokenParams | null>(null);

const stepper = useStepper({
  "available-commerce": {
    title: computed(() => t("pages.events.viewing_conditions")),
    rootModalStyle: "max-w-[41rem] max-h-[90vh] !bg-[var(--base-popup-secondary-bgColor)]",
  },
  "is-buying-commerce": {
    title: "",
    rootModalStyle: "max-w-[30rem] max-h-[90vh] !bg-[var(--base-popup-secondary-bgColor)]",
  },
  "success-purchased": {
    title: "",
    rootModalStyle: "max-w-[30rem] !bg-[var(--base-popup-secondary-bgColor)]",
  },
  "error-purchased": {
    title: "",
    rootModalStyle: "max-w-[30rem] !bg-[var(--base-popup-secondary-bgColor)]",
  },
});

const onSuccessPurchased = () => {
  userCommerce.successPurchased = true;
  noModalBackDropClick.value = false;
  stepper.goTo("success-purchased");
};
const onErrorPaymentInit = (error: Error) => {
  const { message } = getErrorLocalePathMessage(error.message, { t, te });
  return toaster.error(message);
};
const onErrorPurchased = () => {
  noModalBackDropClick.value = false;
  stepper.goTo("error-purchased");
};

const { data: userCreditCard, run: loadCreditCard } = useAsync(BillingAPI.load);
const { loading: loadingInitialization, run: initPayment } = useAsync(BillingAPI.initPayment, { onError: onErrorPaymentInit });

const loading = computed(() => loadingInitialization.value);

const onClickBuyCommerce = (subscription: IModelCommerce) => {
  if (hasMercadoPagoPayment) {
    userCommerce.activeChosenSubscription = subscription;
    stepper.steps.value["is-buying-commerce"].title = t("pages.account_subscription.payment_details", { title: subscription.title });
    return stepper.goToNext();
  }

  const params = {
    redirectURL: route.fullPath,
    title: subscription.title,
    productId: subscription.id,
  };

  const callBackURL = getCallbackCommerceUrl(params);
  return initPayment(subscription.id, callBackURL);
};
const onClickClose = async () => {
  if (userCommerce.successPurchased) {
    await eventStore.load(route.params.eventId as string);
    eventStore.canReloadSuggested = true;
    eventStore.isEventStartingSoon = false;
  }

  userCommerce.activeChosenSubscription = null;
  userCommerce.successPurchased = false;
  stepper.goTo("available-commerce");
};
const onClickGoToEvent = async (callBack: () => void) => {
  await eventStore.load(route.params.eventId as string);
  eventStore.canReloadSuggested = true;
  eventStore.isEventStartingSoon = false;
  callBack();
};

const onPaySubmit = (creditCard: IModelCreditCard) => {
  newCreditCard.value = creditCard;
};

const onPayLoading = () => noModalBackDropClick.value = !noModalBackDropClick.value;

onMounted(() => loadCreditCard());
</script>

<template>
  <BaseModal
    :root-class="stepper.current.value.rootModalStyle" :title="stepper.current.value.title"
    :no-back-drop-click="noModalBackDropClick || loading" @close="onClickClose"
  >
    <template #before-title>
      <slot name="before-title" />
    </template>
    <template #trigger="{ openModal }">
      <slot name="trigger" :open-modal="openModal" />
    </template>
    <template #actions="{ closeModal }">
      <EventCommerceOptions
        v-if="stepper.isCurrent('available-commerce')"
        :disabled="loading"
        @buy-commerce="onClickBuyCommerce"
      />

      <BillingForm
        v-if="stepper.isCurrent('is-buying-commerce') && userCommerce.activeChosenSubscription"
        :subscription="userCommerce.activeChosenSubscription"
        :user-credit-card="userCreditCard"
        outline
        @loading="onPayLoading"
        @success="onSuccessPurchased"
        @error="onErrorPurchased"
        @submit="onPaySubmit"
      >
        <template #cancel="{ disabled }">
          <BaseButton full-width link name="modalConfirm" class="mt-6" :disabled="disabled" @click="onClickClose">
            {{ t('labels.change_product') }}
          </BaseButton>
        </template>
      </BillingForm>

      <BillingSuccess
        v-if="stepper.isCurrent('success-purchased')"
        :title="userCommerce.activeChosenSubscription?.title"
        @redirect="onClickGoToEvent(closeModal)"
      />

      <BillingError
        v-if="stepper.isCurrent('error-purchased')"
        :loading="loading"
        @enter-another-card="stepper.goTo('is-buying-commerce')"
      />
    </template>
  </BaseModal>
</template>
